<script lang="ts" setup>
import type { ISidebarThreadItem } from '@/api/modules/threads/types'
import TextView from '@/ui/common/text-view/TextView.vue'
import ContextMenu from '@/ui/common/context-menu/ContextMenu.vue'
import { computed, ref, watch, nextTick, onUnmounted } from 'vue'

interface MenuItemConfig {
  key: string
  label: string
  icon: string
  onClick: (id: string) => void
  color: string
  isLoading?: boolean
  isDisabled?: boolean
}

interface Props {
  threadItem: ISidebarThreadItem
  isSelected?: boolean
  menuItems?: MenuItemConfig[]
  isHovered?: boolean
  whenClickThreadItem?: () => void
  isContextMenuOpen?: boolean
  isEditing?: boolean
  editValue?: string
}

interface Emits {
  (e: 'context-menu-open', isOpen: boolean): void
  (e: 'edit-input', value: string): void
  (e: 'edit-save'): void
  (e: 'edit-cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  menuItems: () => [],
  isHovered: false,
  isContextMenuOpen: false,
  isEditing: false,
  isSelected: false,
})

const emit = defineEmits<Emits>()

const editInputRef = ref<HTMLInputElement>()
const editWrapperRef = ref<HTMLElement>()

const shouldShowMenu = computed(() => {
  if (props.isEditing) return false

  if (props.menuItems.length === 0) return false

  return props.isContextMenuOpen || props.isHovered
})

const handleDocumentClick = (event: MouseEvent) => {
  if (!props.isEditing || !editWrapperRef.value) return

  const target = event.target as Node
  const clickedInside = editWrapperRef.value.contains(target)

  if (!clickedInside) {
    emit('edit-save')
  }
}

watch(
  () => props.isEditing,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        editInputRef.value?.focus()
        editInputRef.value?.select()
        setTimeout(() => {
          document.addEventListener('click', handleDocumentClick)
        }, 10)
      })
    } else {
      document.removeEventListener('click', handleDocumentClick)
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})

const handleContextMenuOpen = (open: boolean) => {
  emit('context-menu-open', open)
}

const handleInputKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    emit('edit-save')
  } else if (e.key === 'Escape') {
    emit('edit-cancel')
  }
}

const handleInputBlur = () => {
  emit('edit-save')
}

const handleInput = (e: Event) => {
  emit('edit-input', (e.target as HTMLInputElement).value)
}

const handleWrapperClick = (e: MouseEvent) => {
  e.stopPropagation()
}
</script>

<template>
  <div
    :class="[
      'ThreadItem',
      props.isSelected && 'ThreadItem_selected',
      props.isEditing && 'ThreadItem_editing',
    ]"
    @click="!isEditing && props.whenClickThreadItem?.()"
  >
    <div class="ThreadItem__content">
      <div
        v-if="props.isEditing"
        ref="editWrapperRef"
        class="ThreadItem__editWrapper"
        @click="handleWrapperClick"
      >
        <input
          ref="editInputRef"
          :value="props.editValue"
          @input="handleInput"
          class="ThreadItem__editInput"
          @keydown="handleInputKeydown"
          @blur="handleInputBlur"
        />
        <div
          v-if="props.menuItems.find((item) => item.key === 'rename')?.isLoading"
          class="ThreadItem__editSpinner"
        ></div>
      </div>

      <TextView
        v-else
        variant="roboto_13_regular"
        :class="['ThreadItem__textView', props.isSelected && 'ThreadItem__textView_selected']"
      >
        {{ props.threadItem.last_message }}
      </TextView>
    </div>
    <div :class="['ThreadItem__menu', shouldShowMenu && 'ThreadItem__menu_visible']">
      <ContextMenu
        v-if="menuItems.length > 0"
        :menu-items="props.menuItems"
        trigger="click"
        @open-change="handleContextMenuOpen"
      />
    </div>
  </div>
</template>

<style lang="scss">
.ThreadItem {
  position: relative;
  cursor: pointer;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 25px;
  width: 100%;

  &__content {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  &__editWrapper {
    position: relative;
    width: 100%;
  }

  &__editInput {
    width: 100%;
    border: 1px solid var(--brand_50);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 13px;
    font-family: 'Roboto', sans-serif;
    color: var(--gray_80);
    background: var(--white);
    outline: none;
    box-sizing: border-box;

    &:focus {
      border-color: var(--brand_70);
      box-shadow: 0 0 0 2px rgba(0, 90, 255, 0.1);
    }
  }

  &__editSpinner {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    border: 2px solid var(--gray_30);
    border-top-color: var(--brand_50);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__textView {
    padding: 6px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: block;
  }

  &__textView_selected {
    color: var(--brand_50);
  }

  &__menu {
    flex-shrink: 0;
    opacity: 0;
    visibility: hidden;
    width: 24px;
    transition: opacity 0.2s ease;
  }

  &__menu_visible {
    opacity: 1;
    visibility: visible;
  }

  &:hover {
    .ThreadItem__textView {
      color: var(--brand_50);
    }
  }

  &_selected::after {
    content: '';
    position: absolute;
    width: 4px;
    height: 32px;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNCIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDQgMzIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0wIDRDMCAxLjc5MDg2IDEuNzkwODYgMCA0IDBWMzJDMS43OTA4NiAzMiAwIDMwLjIwOTEgMCAyOFY0WiIgZmlsbD0iIzAwNUFGRiIvPgo8L3N2Zz4K');
    border-radius: 4px 0 0 4px;
    z-index: 6;
  }

  &_editing {
    cursor: default;

    &:hover {
      .ThreadItem__textView {
        color: inherit;
      }
    }

    &::after {
      display: none;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
